import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "../../i18n/I18nContext";

const STATUS_COLORS = ["#487FFF", "#FF9F29", "#45B369", "#8E5FE4", "#F1416C"];

const TotalIncome = ({ dashboardData, loading }) => {
  const { t } = useTranslation();

  const statusLabels = useMemo(
    () => ({
      NEW: t('dashboard.registrationStatus.new'),
      UNLOCKED: t('dashboard.registrationStatus.unlocked'),
      LOCKED: t('dashboard.registrationStatus.locked'),
      RENEW: t('dashboard.registrationStatus.renew'),
      ACTIVE: t('dashboard.registrationStatus.active'),
    }),
    [t],
  );

  const statusData = useMemo(() => {
    const registrationsByStatus = (dashboardData && dashboardData.registrationsByStatus) || [];
    return registrationsByStatus.map((entry) => ({
      label: statusLabels[entry.status] || entry.status,
      count: parseInt(entry.count || 0, 10),
    }));
  }, [dashboardData, statusLabels]);

  const total = statusData.reduce((sum, entry) => sum + entry.count, 0);

  const chartOptions = {
    colors: STATUS_COLORS,
    labels: statusData.map((entry) => entry.label),
    legend: { show: false },
    chart: {
      type: "donut",
      height: 260,
      sparkline: { enabled: true },
    },
    stroke: { width: 0 },
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (value) => `${value}` } },
    responsive: [
      { breakpoint: 480, options: { chart: { width: 200 }, legend: { position: "bottom" } } },
    ],
  };

  const chartSeries = statusData.map((entry) => entry.count);

  return (
    <div className='col-xxl-12 col-xl-6'>
      <div className='card h-100 radius-8 border-0'>
        <div className='card-header border-bottom d-flex align-items-center flex-wrap gap-2 justify-content-between'>
          <h6 className='mb-2 fw-bold text-lg'>{t('dashboard.registrationStatus.title')}</h6>
        </div>
        <div className='card-body p-24'>
          <div className='position-relative'>
            <div
              id='statisticsDonutChart'
              className='mt-36 flex-grow-1 apexcharts-tooltip-z-none title-style circle-none'
            >
              {!loading && total > 0 ? (
                <ReactApexChart
                  options={chartOptions}
                  series={chartSeries}
                  type='donut'
                  height={260}
                />
              ) : (
                <div className='text-center text-secondary-light py-64'>
                  {loading ? t('dashboard.loading') : t('dashboard.registrations.empty')}
                </div>
              )}
            </div>
            {total > 0 && (
              <div className='text-center position-absolute top-50 start-50 translate-middle'>
                <span className='text-secondary-light'>{t('dashboard.total')}</span>
                <h6 className=''>{total}</h6>
              </div>
            )}
          </div>
          <ul className='row gy-4 mt-3'>
            {statusData.map((entry, i) => (
              <li key={entry.label} className='col-6 d-flex flex-column align-items-center'>
                <div className='d-flex align-items-center gap-2'>
                  <span
                    className='w-12-px h-8-px rounded-pill'
                    style={{ background: STATUS_COLORS[i % STATUS_COLORS.length] }}
                  />
                  <span className='text-secondary-light text-sm fw-normal'>{entry.label}</span>
                </div>
                <h6 className='text-primary-light fw-bold mb-0'>{entry.count}</h6>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TotalIncome;
